import { VocabularyTerms } from '@ftr/contracts/type/core'
import { LocalTimeRange } from '@ftr/contracts/type/shared'
import { assertUnreachable } from '@ftr/foundation'
import { LocalTime } from '@js-joda/core'
import {
  DifferentSessionsValidationReason,
  TimeValidation,
  TimeValidationReason,
  capitalizeFirstLetter,
  formatSuggestedTime,
  getEnclosingTimeRangeForEndTime,
  getEnclosingTimeRangeForStartTime,
  isFutureEndTime,
  runValidators,
  validateBoundaries,
  validateSameSession,
  validateSectionTimes,
} from '../time-validation-utils'

function outOfBoundsMessage(type: 'start' | 'end', suggestedTime: LocalTime | undefined): string {
  return suggestedTime
    ? `${capitalizeFirstLetter(type)} time is not within a session. This session ${type}s at ${formatSuggestedTime(suggestedTime)}.`
    : `${capitalizeFirstLetter(type)} time is not within a session.`
}

export type SealedTimeValidationReason = TimeValidationReason | DifferentSessionsValidationReason

export type SealedTimeValidation = TimeValidation<SealedTimeValidationReason>

/**
 * behaviour
 *
 * if startTime: start inclusive/end exclusive
 * if endTime: start exclusive/end inclusive
 */
export function validateSealedSectionTimes(
  startTime: LocalTime,
  endTime: LocalTime,
  onRecordTimeFrames: LocalTimeRange[],
  liveLocaltime: LocalTime | undefined,
): SealedTimeValidation | undefined {
  const startBoundary = getEnclosingTimeRangeForStartTime(startTime, onRecordTimeFrames)
  const endBoundary = getEnclosingTimeRangeForEndTime(endTime, onRecordTimeFrames)

  const validators: (() => SealedTimeValidation | undefined)[] = [
    () => validateSectionTimes(startTime, endTime),
    () => validateBoundaries(startBoundary, endBoundary),
    () => validateSameSession(startBoundary!, endBoundary!),
    () => isFutureEndTime(endTime, endBoundary!, liveLocaltime),
  ]

  return runValidators(validators)
}

export function mapSealedTimeValidationReasonMessage(
  errorValidation: SealedTimeValidation,
  terms: VocabularyTerms,
): string {
  switch (errorValidation.reason) {
    case 'start-is-out-of-bounds':
      return outOfBoundsMessage('start', errorValidation.matchingEndBoundary?.start)
    case 'end-is-out-of-bounds':
      return outOfBoundsMessage('end', errorValidation.matchingStartBoundary?.end)
    case 'both-out-of-bounds':
      return 'Start and end times are not within a session.'
    case 'different-sessions':
      return 'Start and end times are in different sessions.'
    case 'start-must-be-before-end':
      return 'Start time must be before end time.'
    case 'future-end-time':
      return `A ${terms.sealed.singular} segment's end time cannot be in the future.`
    case 'start-is-not-a-time':
      return 'Please enter a valid start time.'
    case 'end-is-not-a-time':
      return 'Please enter a valid end time.'
    case 'both-are-not-times':
      return 'Please enter valid times.'
    default:
      return assertUnreachable(errorValidation.reason)
  }
}
